import {
  InputChangeEventDetail,
  IonButton,
  IonContent,
  IonIcon,
  IonPage,
} from '@ionic/react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Auth } from 'aws-amplify';
import { warningOutline } from 'ionicons/icons';
import { ReduxCommandCreator } from 'appRedux/actions';
import { IonInputCustomEvent } from '@ionic/core';
import { useRef, useState, useEffect } from 'react';
import CasinoNavBar from 'components/CasinoNavBar';
import { MobileNumberField } from '../../components/MobileNumberField';
import { useSupportedPhoneCountries } from 'common/reactHooks';
import {
  formatPhoneNumberForCountry,
  getDefaultSupportedPhoneCountryId,
  getInternationalPhoneNumber,
  getPhoneDisplayValue,
  getPhoneInputMaxLength,
  getSupportedPhoneCountry,
  isPhoneNumberValidForCountry,
  SupportedPhoneCountryId,
} from '../../../../common/phoneCountries';

export const ForgotPassword = () => {
  const dispatch = useDispatch();
  const commands = ReduxCommandCreator(dispatch);
  const history = useHistory();
  const inputEl = useRef<HTMLIonInputElement>(null);

  const [phone, setPhone] = useState('');
  const [phoneErr, setPhoneErr] = useState('');
  const [selectedCountryId, setSelectedCountryId] = useState<SupportedPhoneCountryId | undefined>();
  const { supportedPhoneCountries } = useSupportedPhoneCountries();
  const selectedCountry = getSupportedPhoneCountry(selectedCountryId, supportedPhoneCountries);

  useEffect(() => {
    const defaultCountryId = getDefaultSupportedPhoneCountryId(supportedPhoneCountries);
    const hasSelectedCountry = supportedPhoneCountries.some(
      (country) => country.shortLabel === selectedCountryId
    );
    if ((!selectedCountryId || !hasSelectedCountry) && defaultCountryId) {
      setSelectedCountryId(defaultCountryId);
    }
  }, [selectedCountryId, supportedPhoneCountries]);

  const formatPhone = (ev: IonInputCustomEvent<InputChangeEventDetail>) => {
    if (!selectedCountry) return;
    const value = `${ev.target.value ?? ''}`;
    const filteredValue = formatPhoneNumberForCountry(value, selectedCountry);
    setPhone(filteredValue);
    if (inputEl.current !== null) inputEl.current.value = filteredValue;
  };

  const onCountryChange = (countryId: SupportedPhoneCountryId) => {
    const nextCountry = getSupportedPhoneCountry(countryId, supportedPhoneCountries);
    if (!nextCountry) return;
    setSelectedCountryId(countryId);
    setPhoneErr('');
    const reformattedPhone = formatPhoneNumberForCountry(phone, nextCountry);
    setPhone(reformattedPhone);
    if (inputEl.current !== null) inputEl.current.value = reformattedPhone;
  };

  const onSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!selectedCountry) {
      setPhoneErr('Mobile country options are loading');
      return;
    }
    if (!phone) {
      setPhoneErr('Cell number is required');
      return;
    } else {
      setPhoneErr('');
    }
    const validatePhNo = isPhoneNumberValidForCountry(phone, selectedCountry);
    if (!validatePhNo) {
      setPhoneErr(`Please enter a valid ${selectedCountry.label.toLowerCase()} mobile number`);
      return;
    }

    const internationalPhoneNumber = getInternationalPhoneNumber(phone, selectedCountry);
    const phoneDisplayValue = getPhoneDisplayValue(phone, selectedCountry);

    void Auth.forgotPassword(`+${internationalPhoneNumber}`);
    commands.SetUserInfo(internationalPhoneNumber, phoneDisplayValue);
    history.push('/auth/resetpassword');
  };

  return (
    <IonPage className="login-v2-page">
      <IonContent fullscreen>
        <div className="login-v2-wrapper">
          <div className="casino-fixed-top__nav login-v2-nav-wrap">
            <CasinoNavBar hideSearch hideLogo hideMenu />
            <button
              className="login-v2-back-btn"
              onClick={() => history.goBack()}
              aria-label="Go back"
            />
          </div>
          <div className="login-v2-bg">
            <div className="login-v2-body">
              <h1 className="login-v2-welcome">FORGOT PASSWORD</h1>
              <div className="login-v2-form">
                <form onSubmit={onSubmit}>
                  {/* Phone field */}
                  <div className="login-v2-field-wrap login-v2-phone-wrap">
                    <div className="login-v2-phone-content">
                      <MobileNumberField
                        value={phone}
                        onChange={formatPhone}
                        maxLength={selectedCountry ? getPhoneInputMaxLength(selectedCountry) : 0}
                        placeholder="Enter phone number"
                        inputRef={inputEl}
                        selectedCountryId={selectedCountryId}
                        selectedCountryDialCode={selectedCountry?.dialCode}
                        onCountryChange={onCountryChange}
                        countryOptions={supportedPhoneCountries}
                      />
                    </div>
                  </div>
                  {phoneErr && (
                    <div className="login-v2-field-error">
                      <IonIcon icon={warningOutline} />
                      <span>{phoneErr}</span>
                    </div>
                  )}

                  <IonButton type="submit" expand="block" className="login-v2-submit-btn">
                    Send Reset Code
                  </IonButton>
                </form>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};
